import React, { useState } from 'react';

import { StepperFooter, StepperContent, ProgressHeader, IndicatorHeader } from '../..';
import * as StepperTypes from '.';

const headers = {
    indicator: IndicatorHeader,
    progress: ProgressHeader,
};

/**
 * The `Stepper` is intended to guide a user through a multiple-step process. 
 * 
 * * The `type` property allow you to specify the `Stepper` you wish to render. We prefer to use `indicator`, also the default, on Desktop and `progress` for Mobile layouts.
 * 
 * * The `footer` property allows for a multitude of styling, layout, and functional configurations by accepting a custom component.
 * If the property is undefined, the default footer will be rendered in its place.  
 * 
 * * The `steps` property accepts an array of items (see props table below for more detail) that can optionally specify a `stepAction` property. If specified, the `stepAction` will be executed before
 * advancing to the next step. This could include anything from submitting form data, dispatching an alert, or displaying a confirmation modal.
 * 
 */

export const Stepper = ({ startingStep, steps, footer, type, className }: StepperTypes.Stepper) => {
    const [activeStep, setActiveStep] = useState(startingStep || 0);
    // We need to make sure we only render the conponent when the activeStep exists in the array of steps.
    // This prevents a re-render on the final step
    if (!steps || !steps[activeStep]) return null;
    const handleNext = () => {
        if (steps[activeStep].stepAction) steps[activeStep].stepAction();
        steps[activeStep].completed = true;
        setActiveStep(prevActiveStep => prevActiveStep + 1);
    };
    const handleBack = () => setActiveStep(prevActiveStep => prevActiveStep - 1);
    const StepperHeader = headers[type];
    let HeaderText: React.ReactNode | undefined;
    if (steps[activeStep].customHeader) {
        HeaderText = (
            <div id="custom-header">
                {steps[activeStep].customHeader}
            </div>
        );
    }
    return (
        <div className={`d-flex h-100 flex-column align-content-stretch pb-4 ${className}`}>
            <StepperHeader {...{ steps, activeStep }} />
            {HeaderText}
            <StepperContent className="d-flex flex-grow-1 flex-shrink-0  my-3 mx-2" step={steps[activeStep]} />
            <StepperFooter
                {...{ steps, footer, activeStep, handleBack, handleNext }}
                backButtonText="Back"
                nextButtonText={steps[activeStep].nextButtonText || 'Next'}
            />
        </div>
  );
};

Stepper.defaultProps = {
    type: 'indicator',
    className: '',
}

export default Stepper;


